// =============================================================================
// GameClient — single composition root that wires together ApiClient,
// AuthService, DataLoader, and ColyseusClient.
//
//   const game = new GameClient({
//     httpUrl: "https://digimon.digital",
//     wsUrl:   "wss://digimon.digital",
//     onAuthFailed: () => router.push("/login"),
//   });
//   await game.boot();
//   const data = game.data.current()!;
//   const me = await game.api.get<Me>("/me");
//
// Pass `game` down through scenes / HUD via React context or props.
// =============================================================================

import { ApiClient } from "./ApiClient";
import { AuthService } from "./AuthService";
import { ColyseusClient } from "./ColyseusClient";
import { DataLoader } from "./DataLoader";

export interface GameClientOptions {
  httpUrl: string;
  wsUrl: string;
  onAuthFailed?: () => void;
}

export interface GameClientServices {
  api: ApiClient;
  auth: AuthService;
  data: DataLoader;
  colyseus: ColyseusClient;
}

export class GameClient implements GameClientServices {
  readonly api: ApiClient;
  readonly auth: AuthService;
  readonly data: DataLoader;
  readonly colyseus: ColyseusClient;
  private readonly opts: GameClientOptions;

  constructor(opts: GameClientOptions) {
    this.opts = opts;
    this.api = new ApiClient({ baseUrl: opts.httpUrl });
    this.auth = new AuthService(this.api);
    this.api.setTokenProvider(this.auth.asTokenProvider(opts.onAuthFailed));
    this.data = new DataLoader(this.api);
    this.colyseus = new ColyseusClient({
      url: opts.wsUrl,
      ...(opts.onAuthFailed ? { onAuthFailed: opts.onAuthFailed } : {}),
    });
  }

  /** Quick health probe + static-data load. Returns `false` if backend is down. */
  async boot(): Promise<{ healthy: boolean; dataLoaded: boolean }> {
    const healthy = await this.healthCheck();
    if (!healthy) return { healthy: false, dataLoaded: false };
    try {
      await this.data.load();
      return { healthy: true, dataLoaded: true };
    } catch {
      return { healthy: true, dataLoaded: false };
    }
  }

  async healthCheck(): Promise<boolean> {
    try {
      await this.api.get("/health", { noAuth: true, timeoutMs: 5000 });
      return true;
    } catch {
      return false;
    }
  }
}
